import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // Send the httpOnly auth cookie with every request (dual-mode: cookie + Bearer fallback).
  withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('evotech_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      const url = error.config?.url || '';
      // Never hijack the login attempt itself — let the caller surface the error.
      const isLoginRequest = url.includes('/auth/login');
      // Only force a redirect if the user has a token (i.e. was authenticated) and the session broke.
      const hasToken = !!localStorage.getItem('evotech_token');
      if (hasToken && !isLoginRequest) {
        localStorage.removeItem('evotech_token');
        localStorage.removeItem('evotech_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API Services
export const authService = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
};

export const clientService = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

export const supplierService = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

export const supplierCertificationService = {
  list: (params) => api.get('/supplier-certifications', { params }),
  getBySupplier: (supplierId) => api.get(`/suppliers/${supplierId}/certifications`),
  create: (supplierId, data) => api.post(`/suppliers/${supplierId}/certifications`, data),
  update: (id, data) => api.put(`/supplier-certifications/${id}`, data),
  delete: (id) => api.delete(`/supplier-certifications/${id}`),
};

export const productService = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

export const productRevisionService = {
  getAll: (params) => api.get('/product-revisions', { params }),
  getById: (id) => api.get(`/product-revisions/${id}`),
  create: (data) => api.post('/product-revisions', data),
  update: (id, data) => api.put(`/product-revisions/${id}`, data),
};

export const workCycleService = {
  getAll: (params) => api.get('/work-cycles', { params }),
  getById: (id) => api.get(`/work-cycles/${id}`),
  create: (data) => api.post('/work-cycles', data),
  update: (id, data) => api.put(`/work-cycles/${id}`, data),
};

export const workPhaseService = {
  getAll: (params) => api.get('/work-phases', { params }),
  getById: (id) => api.get(`/work-phases/${id}`),
  create: (data) => api.post('/work-phases', data),
  update: (id, data) => api.put(`/work-phases/${id}`, data),
  delete: (id) => api.delete(`/work-phases/${id}`),
};

export const orderService = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  validateExport: (id) => api.get(`/orders/${id}/export-easyfatt/validate`),
  exportEasyfatt: (id) => api.post(`/orders/${id}/export-easyfatt`, {}, { responseType: 'blob' }),
  getExportHistory: (id) => api.get(`/orders/${id}/export-history`),
  sendConfirmation: (id, emailType) => api.post(`/orders/${id}/send-confirmation`, { email_type: emailType || 'confirmation' }),
  getEmailLog: (id) => api.get(`/orders/${id}/email-log`),
  getEmailPreview: (id, emailType) => api.get(`/orders/${id}/email-preview`, { params: { email_type: emailType || 'confirmation' } }),
};

export const emailService = {
  sendTest: (recipient) => api.post('/email/send-test', { recipient }),
  getConfig: () => api.get('/email/config'),
};

export const delayedOrdersReportService = {
  getSettings: () => api.get('/email-reports/delayed-orders/settings'),
  updateSettings: (data) => api.put('/email-reports/delayed-orders/settings', data),
  preview: () => api.get('/email-reports/delayed-orders/preview'),
  sendNow: () => api.post('/email-reports/delayed-orders/send-now'),
  getLog: (limit = 20) => api.get('/email-reports/delayed-orders/log', { params: { limit } }),
};

export const auditTrailService = {
  list: (params) => api.get('/audit-logs', { params }),
  entities: () => api.get('/audit-logs/entities'),
  stats: () => api.get('/audit-logs/stats'),
};

export const instrumentService = {
  getAll: (params) => api.get('/instruments', { params }),
  getById: (id) => api.get(`/instruments/${id}`),
  create: (data) => api.post('/instruments', data),
  update: (id, data) => api.put(`/instruments/${id}`, data),
  delete: (id) => api.delete(`/instruments/${id}`),
  listCalibrations: (id) => api.get(`/instruments/${id}/calibrations`),
  registerCalibration: (id, data) => api.post(`/instruments/${id}/calibrations`, data),
  deleteCalibration: (id) => api.delete(`/calibrations/${id}`),
  overview: (params) => api.get('/calibrations/overview', { params }),
};

export const qualityDashboardService = {
  get: () => api.get('/quality/dashboard'),
};

export const controlRecordService = {
  list: (params) => api.get('/control-records', { params }),
  getById: (id) => api.get(`/control-records/${id}`),
  create: (data) => api.post('/control-records', data),
  update: (id, data) => api.put(`/control-records/${id}`, data),
  delete: (id) => api.delete(`/control-records/${id}`),
  submit: (id) => api.post(`/control-records/${id}/submit`),
  approve: (id) => api.post(`/control-records/${id}/approve`),
  reject: (id, reason) => api.post(`/control-records/${id}/reject`, null, { params: { reason } }),
  backup: () => api.get('/control-records/admin/backup'),
  migrate: (dryRun = false) => api.post('/control-records/admin/migrate', null, { params: { dry_run: dryRun } }),
};

export const qmsPlanService = {
  getEnums: () => api.get('/qms/plans/enums'),
  list: (params) => api.get('/qms/plans', { params }),
  getById: (id) => api.get(`/qms/plans/${id}`),
  getByProduct: (cc, pc, rev) => api.get(`/qms/plans/by-product/${cc}/${pc}/${rev}`),
  create: (data) => api.post('/qms/plans', data),
  update: (id, data) => api.put(`/qms/plans/${id}`, data),
  duplicate: (id, data) => api.post(`/qms/plans/${id}/duplicate`, data),
  listRows: (planId, includeObsolete = false) => api.get(`/qms/plans/${planId}/rows`, { params: { include_obsolete: includeObsolete } }),
  createRow: (planId, data) => api.post(`/qms/plans/${planId}/rows`, data),
  updateRow: (planId, rowId, data) => api.put(`/qms/plans/${planId}/rows/${rowId}`, data),
  obsoleteRow: (planId, rowId) => api.put(`/qms/plans/${planId}/rows/${rowId}/obsolete`),
  updateApplicability: (planId, rowId, applicabilita) => api.put(`/qms/plans/${planId}/rows/${rowId}/applicability`, { applicabilita }),
  approve: (planId) => api.post(`/qms/plans/${planId}/approve`),
  exportCsv: (planId) => api.get(`/qms/plans/${planId}/rows/export`, { responseType: 'blob' }),
  templateCsv: () => api.get('/qms/plans/template/csv', { responseType: 'blob' }),
  importCsv: (planId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/qms/plans/${planId}/rows/import`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const qmsCheckService = {
  getEnums: () => api.get('/qms/checks/enums'),
  listCmmInstruments: () => api.get('/qms/checks/instruments/by-type'),
  list: (params) => api.get('/qms/checks', { params }),
  getById: (id) => api.get(`/qms/checks/${id}`),
  create: (data) => api.post('/qms/checks', data),
  update: (id, data) => api.put(`/qms/checks/${id}`, data),
  getApplicableRows: (params) => api.get('/qms/checks/applicable-rows', { params }),
  previewConformity: (data) => api.post('/qms/checks/preview-conformity', data),
  previewOverall: (data) => api.post('/qms/checks/preview-overall', data),
  pdfSingle: (id) => api.get(`/qms/checks/${id}/pdf`, { responseType: 'blob' }),
  exportExcel: (params) => api.get('/qms/exports/checks/excel', { params, responseType: 'blob' }),
  exportPdf: (params) => api.get('/qms/exports/checks/pdf', { params, responseType: 'blob' }),
};

export const qmsRqpService = {
  getEnums: () => api.get('/qms/production-requests/enums'),
  list: (params) => api.get('/qms/production-requests', { params }),
  getById: (id) => api.get(`/qms/production-requests/${id}`),
  createFromCheck: (data) => api.post('/qms/production-requests/from-check', data),
  createManual: (data) => api.post('/qms/production-requests/manual', data),
  update: (id, data) => api.put(`/qms/production-requests/${id}`, data),
  registerReturn: (id, data) => api.post(`/qms/production-requests/${id}/return`, data),
  recontrol: (id, data) => api.post(`/qms/production-requests/${id}/recontrol`, data),
  close: (id, data) => api.post(`/qms/production-requests/${id}/close`, data),
  cancel: (id, data) => api.post(`/qms/production-requests/${id}/cancel`, data),
  pdf: (id) => api.get(`/qms/production-requests/${id}/pdf`, { responseType: 'blob' }),
};

export const qualityControlReportService = {
  list: (params) => api.get('/quality-control-reports', { params }),
  getById: (id) => api.get(`/quality-control-reports/${id}`),
  create: (data) => api.post('/quality-control-reports', data),
  update: (id, data) => api.put(`/quality-control-reports/${id}`, data),
  remove: (id) => api.delete(`/quality-control-reports/${id}`),
  updateSample: (id, sampleId, data) => api.put(`/quality-control-reports/${id}/samples/${sampleId}`, data),
  saveMeasurements: (id, sampleId, measurements) => api.put(`/quality-control-reports/${id}/samples/${sampleId}/measurements`, { measurements }),
  autosaveMeasurement: (id, data) => api.put(`/quality-control-reports/${id}/measurements/one`, data),
  syncMeasurements: (id, items) => api.post(`/quality-control-reports/${id}/sync`, { items }),
  complete: (id) => api.post(`/quality-control-reports/${id}/complete`),
  reopen: (id) => api.post(`/quality-control-reports/${id}/reopen`),
  cancel: (id) => api.post(`/quality-control-reports/${id}/cancel`),
  sign: (id, data) => api.post(`/quality-control-reports/${id}/signature`, data),
  deleteSignature: (id, role) => api.delete(`/quality-control-reports/${id}/signature/${role}`),
  createPendingCollaudo: (id, data) => api.post(`/quality-control-reports/${id}/pending-collaudo`, data),
  pdf: (id) => api.get(`/quality-control-reports/${id}/report.pdf`, { responseType: 'blob' }),
};



export const orderLineService = {
  getAll: (params) => api.get('/order-lines', { params }),
  create: (data) => api.post('/order-lines', data),
  update: (id, data) => api.put(`/order-lines/${id}`, data),
  delete: (id) => api.delete(`/order-lines/${id}`),
};

export const productionLotService = {
  getAll: (params) => api.get('/production-lots', { params }),
  getById: (id) => api.get(`/production-lots/${id}`),
  create: (data) => api.post('/production-lots', data),
  update: (id, data) => api.put(`/production-lots/${id}`, data),
  delete: (id) => api.delete(`/production-lots/${id}`),
};

export const lotProgressService = {
  getAll: (params) => api.get('/lot-progress', { params }),
  create: (data) => api.post('/lot-progress', data),
  update: (id, data) => api.put(`/lot-progress/${id}`, data),
};

export const controlPlanService = {
  getAll: (params) => api.get('/control-plans', { params }),
  getById: (id) => api.get(`/control-plans/${id}`),
  create: (data) => api.post('/control-plans', data),
  update: (id, data) => api.put(`/control-plans/${id}`, data),
  delete: (id) => api.delete(`/control-plans/${id}`),
};

export const executedControlService = {
  getAll: (params) => api.get('/executed-controls', { params }),
  getById: (id) => api.get(`/executed-controls/${id}`),
  create: (data) => api.post('/executed-controls', data),
  update: (id, data) => api.put(`/executed-controls/${id}`, data),
};

export const nonConformityService = {
  getAll: (params) => api.get('/non-conformities', { params }),
  getById: (id) => api.get(`/non-conformities/${id}`),
  create: (data) => api.post('/non-conformities', data),
  update: (id, data) => api.put(`/non-conformities/${id}`, data),
  delete: (id) => api.delete(`/non-conformities/${id}`),
  generatePdf: (id, mode = 'dossier') => api.get(`/non-conformities/${id}/generate-pdf`, { params: { mode }, responseType: 'blob' }),
};

export const ncCostService = {
  getAll: (params) => api.get('/nc-costs', { params }),
  create: (data) => api.post('/nc-costs', data),
  update: (id, data) => api.put(`/nc-costs/${id}`, data),
  delete: (id) => api.delete(`/nc-costs/${id}`),
};

export const debitNoteService = {
  getAll: (params) => api.get('/debit-notes', { params }),
  getById: (id) => api.get(`/debit-notes/${id}`),
  create: (data) => api.post('/debit-notes', data),
  update: (id, data) => api.put(`/debit-notes/${id}`, data),
  delete: (id) => api.delete(`/debit-notes/${id}`),
  getPrintData: (id) => api.get(`/debit-notes/${id}/print-data`),
};

export const debitNoteLineService = {
  getAll: (debitNoteId) => api.get('/debit-note-lines', { params: { debit_note_id: debitNoteId } }),
  create: (data) => api.post('/debit-note-lines', data),
  update: (id, data) => api.put(`/debit-note-lines/${id}`, data),
  delete: (id) => api.delete(`/debit-note-lines/${id}`),
};

export const attachmentService = {
  getAll: (params) => api.get('/attachments', { params }),
  getByIds: (ids) => api.get('/attachments', { params: { ids: ids.join(',') } }),
  create: (data) => api.post('/attachments', data),
  upload: (file, entityType, entityId, category, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    formData.append('category', category);
    formData.append('description', description);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => api.delete(`/attachments/${id}`),
};

export const userService = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  deletePermanent: (id) => api.delete(`/users/${id}`, { params: { permanent: true } }),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { new_password: newPassword }),
  unlock: (id) => api.post(`/users/${id}/unlock`),
  accessHistory: (id, limit = 100) => api.get(`/users/${id}/access-history`, { params: { limit } }),
};

export const dashboardService = {
  getStats: () => api.get('/dashboard/stats'),
  getNcByType: () => api.get('/dashboard/nc-by-type'),
  getNcBySeverity: () => api.get('/dashboard/nc-by-severity'),
  getNcByMacroArea: () => api.get('/dashboard/nc-by-macro-area'),
  getLotsByStatus: () => api.get('/dashboard/lots-by-status'),
  getRecentLots: (limit) => api.get('/dashboard/recent-lots', { params: { limit } }),
  getRecentNcs: (limit) => api.get('/dashboard/recent-ncs', { params: { limit } }),
};

export const toolCategoryService = {
  getAll: (params) => api.get('/tool-categories', { params }),
  getById: (id) => api.get(`/tool-categories/${id}`),
  create: (data) => api.post('/tool-categories', data),
  update: (id, data) => api.put(`/tool-categories/${id}`, data),
  delete: (id) => api.delete(`/tool-categories/${id}`),
};

export const toolService = {
  getAll: (params) => api.get('/tools', { params }),
  getById: (id) => api.get(`/tools/${id}`),
  create: (data) => api.post('/tools', data),
  update: (id, data) => api.put(`/tools/${id}`, data),
  delete: (id) => api.delete(`/tools/${id}`),
  getStatusHistory: (id) => api.get(`/tools/${id}/status-history`),
  getLinkedRevisions: (id) => api.get(`/tools/${id}/linked-revisions`),
  getUsageHistory: (id) => api.get(`/tools/${id}/usage-history`),
};

export const revisionToolService = {
  getAll: (params) => api.get('/revision-tools', { params }),
  create: (data) => api.post('/revision-tools', data),
  update: (id, data) => api.put(`/revision-tools/${id}`, data),
  delete: (id) => api.delete(`/revision-tools/${id}`),
  uploadDrawing: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/revision-tools/${id}/drawing`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteDrawing: (id) => api.delete(`/revision-tools/${id}/drawing`),
};

export const lotToolUsageService = {
  getAll: (params) => api.get('/lot-tool-usage', { params }),
  create: (data) => api.post('/lot-tool-usage', data),
  update: (id, data) => api.put(`/lot-tool-usage/${id}`, data),
  delete: (id) => api.delete(`/lot-tool-usage/${id}`),
};

export const pendingCollaudoService = {
  getAll: (params) => api.get('/pending-collaudo', { params }),
  getById: (id) => api.get(`/pending-collaudo/${id}`),
  create: (data) => api.post('/pending-collaudo', data),
  update: (id, data) => api.put(`/pending-collaudo/${id}`, data),
  delete: (id) => api.delete(`/pending-collaudo/${id}`),
};

export const planningService = {
  getAll: (params) => api.get('/planning-activities', { params }),
  getById: (id) => api.get(`/planning-activities/${id}`),
  create: (data) => api.post('/planning-activities', data),
  update: (id, data) => api.put(`/planning-activities/${id}`, data),
  delete: (id) => api.delete(`/planning-activities/${id}`),
};

export const machineService = {
  getAll: (params) => api.get('/machines', { params }),
  getById: (id) => api.get(`/machines/${id}`),
  create: (data) => api.post('/machines', data),
  update: (id, data) => api.put(`/machines/${id}`, data),
  delete: (id) => api.delete(`/machines/${id}`),
  getTypes: () => api.get('/machines-types'),
  getManufacturers: () => api.get('/machines-manufacturers'),
  getDepartments: () => api.get('/machines-departments'),
};

export const maintenancePlanService = {
  getAll: (params) => api.get('/maintenance-plans', { params }),
  getById: (id) => api.get(`/maintenance-plans/${id}`),
  create: (data) => api.post('/maintenance-plans', data),
  update: (id, data) => api.put(`/maintenance-plans/${id}`, data),
  delete: (id) => api.delete(`/maintenance-plans/${id}`),
};

export const maintenanceHistoryService = {
  getAll: (params) => api.get('/maintenance-history', { params }),
  getById: (id) => api.get(`/maintenance-history/${id}`),
  create: (data) => api.post('/maintenance-history', data),
  update: (id, data) => api.put(`/maintenance-history/${id}`, data),
  delete: (id) => api.delete(`/maintenance-history/${id}`),
  getDashboard: () => api.get('/maintenance-dashboard'),
};

export const seedService = {
  seedData: () => api.post('/seed-data'),
};

export const employeeService = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  getDepartments: () => api.get('/employees/departments'),
  getRoles: () => api.get('/employees/roles'),
  addGeneralSkill: (id, data) => api.post(`/employees/${id}/general-skills`, data),
  updateGeneralSkill: (id, skillId, data) => api.put(`/employees/${id}/general-skills/${skillId}`, data),
  deleteGeneralSkill: (id, skillId) => api.delete(`/employees/${id}/general-skills/${skillId}`),
  addProductSkill: (id, data) => api.post(`/employees/${id}/product-skills`, data),
  updateProductSkill: (id, skillId, data) => api.put(`/employees/${id}/product-skills/${skillId}`, data),
  deleteProductSkill: (id, skillId) => api.delete(`/employees/${id}/product-skills/${skillId}`),
};

export const skillsMatrixService = {
  getGeneral: (params) => api.get('/skills-matrix/general', { params }),
  getProduct: (params) => api.get('/skills-matrix/product', { params }),
};

export const trainingService = {
  getAll: (params) => api.get('/training-sessions', { params }),
  getById: (id) => api.get(`/training-sessions/${id}`),
  create: (data) => api.post('/training-sessions', data),
  update: (id, data) => api.put(`/training-sessions/${id}`, data),
  delete: (id) => api.delete(`/training-sessions/${id}`),
  getTrainers: () => api.get('/training-sessions/trainers'),
  getTopics: () => api.get('/training-sessions/topics'),
};

export const trainingSummaryService = {
  byEmployee: () => api.get('/training-summary-v2/by-employee'),
  byPeriod: (params) => api.get('/training-summary/by-period', { params }),
  byTopic: () => api.get('/training-summary/by-topic'),
  byProduct: () => api.get('/training-summary/by-product'),
  forEmployee: (id) => api.get(`/training-summary-v2/employee/${id}`),
};

export const trainingMinuteService = {
  getAll: (params) => api.get('/training-minutes', { params }),
  getById: (id) => api.get(`/training-minutes/${id}`),
  create: (data) => api.post('/training-minutes', data),
  update: (id, data) => api.put(`/training-minutes/${id}`, data),
  delete: (id) => api.delete(`/training-minutes/${id}`),
  updateParticipants: (id, participants) => api.put(`/training-minutes/${id}/participants`, participants),
};

export const revenueService = {
  getByYear: (year) => api.get('/indicator-revenue', { params: { year } }),
  upsert: (data) => api.put('/indicator-revenue', data),
  bulkUpsert: (items) => api.put('/indicator-revenue/bulk', items),
  upsertCustomer: (data) => api.put('/indicator-revenue/customer', data),
  bulkUpsertCustomer: (items) => api.put('/indicator-revenue/customer/bulk', items),
  deleteCustomer: (id) => api.delete(`/indicator-revenue/customer/${id}`),
};

export const personnelService = {
  getByYear: (year) => api.get('/indicator-personnel', { params: { year } }),
  upsert: (data) => api.put('/indicator-personnel', data),
  bulkUpsert: (items) => api.put('/indicator-personnel/bulk', items),
};


export const shipmentService = {
  getAll: (params) => api.get('/shipment-entries', { params }),
  getById: (id) => api.get(`/shipment-entries/${id}`),
  create: (data) => api.post('/shipment-entries', data),
  update: (id, data) => api.put(`/shipment-entries/${id}`, data),
  delete: (id) => api.delete(`/shipment-entries/${id}`),
};

export const clientColorService = {
  getAll: () => api.get('/clients/colors'),
  update: (clientId, color) => api.put(`/clients/${clientId}/color`, { color }),
};


export const commercialOfferService = {
  getAll: (params) => api.get('/commercial-offers', { params }),
  getById: (id) => api.get(`/commercial-offers/${id}`),
  create: (data) => api.post('/commercial-offers', data),
  update: (id, data) => api.put(`/commercial-offers/${id}`, data),
  delete: (id) => api.delete(`/commercial-offers/${id}`),
};

export const commercialDashboardService = {
  getData: (year) => api.get('/commercial-dashboard', { params: { year } }),
};


export const productConfigurationService = {
  getAll: (params) => api.get('/product-configurations', { params }),
  getById: (id) => api.get(`/product-configurations/${id}`),
  create: (data) => api.post('/product-configurations', data),
  update: (id, data) => api.put(`/product-configurations/${id}`, data),
  delete: (id) => api.delete(`/product-configurations/${id}`),
};

export const toolListService = {
  getAll: (params) => api.get('/tool-lists', { params }),
  getById: (id) => api.get(`/tool-lists/${id}`),
  create: (data) => api.post('/tool-lists', data),
  update: (id, data) => api.put(`/tool-lists/${id}`, data),
  delete: (id) => api.delete(`/tool-lists/${id}`),
  newVersion: (id) => api.post(`/tool-lists/${id}/new-version`),
};

export const toolListLineService = {
  getAll: (toolListId) => api.get('/tool-list-lines', { params: { tool_list_id: toolListId } }),
  create: (data) => api.post('/tool-list-lines', data),
  update: (id, data) => api.put(`/tool-list-lines/${id}`, data),
  delete: (id) => api.delete(`/tool-list-lines/${id}`),
};

export const deliveryService = {
  getAll: (params) => api.get('/deliveries', { params }),
  create: (data) => api.post('/deliveries', data),
  update: (id, data) => api.put(`/deliveries/${id}`, data),
  delete: (id) => api.delete(`/deliveries/${id}`),
};

export const apqpService = {
  listProjects: (params) => api.get('/apqp/projects', { params }),
  getProject: (id) => api.get(`/apqp/projects/${id}`),
  createProject: (data) => api.post('/apqp/projects', data),
  updateProject: (id, data) => api.put(`/apqp/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/apqp/projects/${id}`),
  listTasks: (projectId) => api.get(`/apqp/projects/${projectId}/tasks`),
  createTask: (projectId, data) => api.post(`/apqp/projects/${projectId}/tasks`, data),
  updateTask: (taskId, data) => api.put(`/apqp/tasks/${taskId}`, data),
  duplicateTask: (taskId) => api.post(`/apqp/tasks/${taskId}/duplicate`),
  deleteTask: (taskId) => api.delete(`/apqp/tasks/${taskId}`),
  listTemplates: () => api.get('/apqp/templates'),
  createTemplate: (data) => api.post('/apqp/templates', data),
  updateTemplate: (id, data) => api.put(`/apqp/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/apqp/templates/${id}`),
  listSamplings: (projectId) => api.get(`/apqp/projects/${projectId}/samplings`),
  createSampling: (projectId, data) => api.post(`/apqp/projects/${projectId}/samplings`, data),
  updateSampling: (id, data) => api.put(`/apqp/samplings/${id}`, data),
  deleteSampling: (id) => api.delete(`/apqp/samplings/${id}`),
  listBaselines: (projectId) => api.get(`/apqp/projects/${projectId}/baselines`),
  createBaseline: (projectId, data = {}) => api.post(`/apqp/projects/${projectId}/baseline`, data),
  listRevisions: (projectId) => api.get(`/apqp/projects/${projectId}/revisions`),
  createRevision: (projectId, data) => api.post(`/apqp/projects/${projectId}/revisions`, data),
  listDocuments: (projectId) => api.get(`/apqp/projects/${projectId}/documents`),
  createDocument: (projectId, data) => api.post(`/apqp/projects/${projectId}/documents`, data),
  updateDocument: (id, data) => api.put(`/apqp/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/apqp/documents/${id}`),
  populateStandardDocuments: (projectId) => api.post(`/apqp/projects/${projectId}/documents/populate-standard`),
  audit: (projectId, limit = 500) => api.get(`/apqp/projects/${projectId}/audit`, { params: { limit } }),
  dashboard: () => api.get('/apqp/dashboard'),
  projectPdfUrl: (id, variant = 'internal') => `${BACKEND_URL}/api/apqp/projects/${id}/pdf?variant=${variant}`,
  projectExcelUrl: (id) => `${BACKEND_URL}/api/apqp/projects/${id}/excel`,
};


export const simplifiedProgressService = {
  getAll: (lotId) => api.get(`/production-lots/${lotId}/simplified-progress`),
  create: (lotId, data) => api.post(`/production-lots/${lotId}/simplified-progress`, data),
  delete: (id) => api.delete(`/simplified-progress/${id}`),
};


export const customerReceivedNcService = {
  enums: () => api.get('/customer-received-ncs/enums'),
  getAll: (params) => api.get('/customer-received-ncs', { params }),
  getById: (id) => api.get(`/customer-received-ncs/${id}`),
  create: (data) => api.post('/customer-received-ncs', data),
  update: (id, data) => api.put(`/customer-received-ncs/${id}`, data),
  delete: (id) => api.delete(`/customer-received-ncs/${id}`),
  getAttachments: (id) => api.get(`/customer-received-ncs/${id}/attachments`),
  uploadAttachment: (id, file, category, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('attachment_category', category);
    formData.append('description', description);
    return api.post(`/customer-received-ncs/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (ncId, attId) => api.delete(`/customer-received-ncs/${ncId}/attachments/${attId}`),
  reportPdfUrl: (id) => `${BACKEND_URL}/api/customer-received-ncs/${id}/report.pdf`,
  dossierPdfUrl: (id) => `${BACKEND_URL}/api/customer-received-ncs/${id}/dossier.pdf`,
  dashboard: (params) => api.get('/customer-received-ncs/dashboard', { params }),
  comparative: (kind) => api.get('/customer-received-ncs/reports/comparative', { params: { kind } }),
  comparativeXlsxUrl: (kind) => `${BACKEND_URL}/api/customer-received-ncs/reports/comparative.xlsx?kind=${kind}`,
  overdueAlertSettings: {
    get: () => api.get('/customer-received-ncs/overdue-alert/settings'),
    update: (data) => api.put('/customer-received-ncs/overdue-alert/settings', data),
    sendNow: () => api.post('/customer-received-ncs/overdue-alert/send-now'),
  },
};

export const supplierNcService = {
  enums: () => api.get('/supplier-ncs/enums'),
  getAll: (params) => api.get('/supplier-ncs', { params }),
  getById: (id) => api.get(`/supplier-ncs/${id}`),
  create: (data) => api.post('/supplier-ncs', data),
  update: (id, data) => api.put(`/supplier-ncs/${id}`, data),
  delete: (id) => api.delete(`/supplier-ncs/${id}`),
  getAttachments: (id) => api.get(`/supplier-ncs/${id}/attachments`),
  uploadAttachment: (id, file, category, description='') => {
    const fd = new FormData();
    fd.append('file', file); fd.append('attachment_category', category); fd.append('description', description);
    return api.post(`/supplier-ncs/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAttachment: (ncId, attId) => api.delete(`/supplier-ncs/${ncId}/attachments/${attId}`),
  getDebitNote: (id) => api.get(`/supplier-ncs/${id}/debit-note`),
  upsertDebitNote: (id, data) => api.put(`/supplier-ncs/${id}/debit-note`, data),
  deleteDebitNote: (id) => api.delete(`/supplier-ncs/${id}/debit-note`),
  reportPdfUrl: (id) => `${BACKEND_URL}/api/supplier-ncs/${id}/report.pdf`,
  dossierPdfUrl: (id) => `${BACKEND_URL}/api/supplier-ncs/${id}/dossier.pdf`,
  evaluationDashboard: (params) => api.get('/supplier-ncs/dashboard/evaluation', { params }),
  generate8dTemplate: (id) => api.post(`/supplier-ncs/${id}/8d/generate-template`),
  sendToSupplier: (id) => api.post(`/supplier-ncs/${id}/send-to-supplier`),
  emailLog: (id) => api.get(`/supplier-ncs/${id}/email-log`),
  bySupplier: (supplierId, year) => api.get(`/supplier-ncs/by-supplier/${supplierId}`, { params: year ? { year } : {} }),
  generatePortalToken: (id) => api.post(`/supplier-ncs/${id}/generate-portal-token`),
  revokePortalToken: (id) => api.delete(`/supplier-ncs/${id}/portal-token`),
};
